export const colors = {
    primary: {
        main: '#244486',
        dark: 'rgb(6, 43, 106)',
        deep: '#1F3F7A',
        link: '#0045a9',
        light: 'rgba(36, 68, 134, 0.06)',
        tintBorder: 'rgba(36, 68, 134, 0.2)',
        navy: '#0B2E5C',
        darkNavy: '#1e3a5f',
        linkedinBg: '#1a1a2e',
        buttonHover: '#2d3f6b',
        midnight: '#252e4f',
        deepMidnight: '#111b42',
        royalNavy: '#12297d',
        midnightDeep: '#0d1628'
        // The two stops of the AI-acceleration orb: a lit core falling away to the
        // section navy at the rim, so the sphere reads as lit from the centre.
    },
    text: {
        primary: '#111111',
        secondary: '#52637A',
        muted: '#62748E',
        inverse: '#FFFFFF',
        orangeAccessible: '#BE5A00'
    },
    background: {
        default: '#ffffff',
        tag: '#EEF4FF',
        subtle: '#F7F9FC',
        orangeTint: '#FFF6EE',
        cardTray: 'rgba(31, 63, 122, 0.04)',
        // The only green surface in the system — there was no mint/success tint before.
        successTint: '#ECFDF5'
    },
    accent: {
        orange: '#f7933c',
        orangeDark: '#f47a1f',
        orangeTintBorder: '#ffe8cc',
        orangeMid: '#e07b00',
        orangeDeep: '#c96a00',
        orangeDarkest: '#985900',
        blueBorder: '#dce6f7',
        amber: '#f79009',
        starYellow: '#f59e0b',
        // Filled rating stars (review cards, testimonial carousel) — warmer than
        // `starYellow`, which is used for icons and accents.
        starGold: '#FFB800',
        // Clutch's brand red — its rating stars are red, unlike the gold used by the
        // other review platforms.
        starRed: '#FF3D2E',
        gold: '#fdc700',
        goldDark: '#733e0a',
        blue500: '#3b82f6',
        poshBlue: '#0B69FF',
        purple: '#9333ea',
        plumDark: '#352551',
        emeraldDark: '#059669',
        heroPink: '#f6339a',
        indigo: '#4344eb',
        indigoSubtle: '#f1f1ff',
        blueBadgeBorder: 'rgba(81,162,255,0.3)',
        purpleBadgeBorder: 'rgba(194,122,255,0.3)'
    },
    neutral: {
        gray: '#c4c4c4',
        gray999: '#999999',
        grayLight: '#e5e5e5',
        grayMedium: '#d1d5db',
        borderLight: '#e4e7ec',
        borderSubtle: 'rgba(195,198,214,0.3)',
        borderFaint: 'rgba(195,198,214,0.15)',
        slate400: '#94a3b8',
        indigoBorder200: '#d0d1fc'
    },
    brand: {
        linkedin: '#0077B5'
    },
    status: {
        success: '#17B26A',
        successBorder: '#A6F4C5',
        error: '#F04438'
    }
} as const;

export const spacing = {
    4: '0.25rem',
    8: '0.5rem',
    12: '0.75rem',
    16: '1rem',
    20: '1.25rem',
    24: '1.5rem',
    28: '1.75rem',
    32: '2rem',
    40: '2.5rem',
    44: '2.75rem',
    48: '3rem',
    56: '3.5rem',
    60: '3.75rem',
    64: '4rem',
    72: '4.5rem'
} as const;

export const fontSize = {
    10: '0.625rem',
    11: '0.6875rem',
    12: '0.75rem',
    13: '0.8125rem',
    14: '0.875rem',
    15: '0.9375rem',
    16: '1rem',
    18: '1.125rem',
    20: '1.25rem',
    22: '1.375rem',
    24: '1.5rem',
    28: '1.75rem',
    30: '1.875rem',
    32: '2rem',
    34: '2.125rem',
    36: '2.25rem',
    40: '2.5rem',
    42: '2.625rem',
    46: '2.875rem',
    48: '3rem',
    56: '3.5rem',
    65: '4.0625rem'
} as const;

export const lineHeight = {
    20: '1.25rem',
    24: '1.5rem',
    28: '1.75rem',
    32: '2rem',
    40: '2.5rem',
    48: '3rem',
    56: '3.5rem'
} as const;

export const shadows = {
    sm: '0px 0px 10px rgba(0, 0, 0, 0.1)',
    // Soft resting shadow for cards on a light ground. Offset downward with no spread,
    // so nothing bleeds above the card's top edge (unlike `sm`, which is centred).
    card: '0 4px 12px rgba(0, 0, 0, 0.07)',
    lg: '0 4px 20px rgba(0, 0, 0, 0.1)',
    awardCardElevated: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
    header: 'rgba(50, 50, 93, 0.55) 0px 12px 13px -10px',
    elevated: '0 8px 40px rgba(0,0,0,0.10)',
    // Weighted downward: a large y-offset with no spread, so the shadow pools under the
    // element and barely shows at its sides.
    bottomDrop: '0 14px 22px -14px rgba(0,0,0,0.18)',
    ring: '0 0 0 6px rgba(36, 68, 134, 0.1)',
    // Selected-option ring: solid `primary.main`, so a chosen card reads as chosen from
    // across the section rather than as a faint halo.
    ringPrimary: '0 0 0 1px #244486',
    // Hover and pop-over level: one step above `elevated`, for cards that lift on hover,
    // tooltips and floating panels.
    raised: '0 12px 32px rgba(0, 0, 0, 0.16)',
    // Even halo with spread, for framed media (screenshots, bordered accordions).
    glow: '0 0 16px 5px rgba(0, 0, 0, 0.15)',
    // Keyboard/focus ring for form fields.
    focusRing: '0 0 0 3px #d6e4f6'
} as const;

export const borderRadius = {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.25rem',
    '3xl': '1.5rem',
    full: '9999px',
    circle: '50%'
} as const;

export const overlay = {
    whiteHint: 'rgba(255,255,255,0.03)',
    whiteSubtle: 'rgba(255,255,255,0.08)',
    whiteFaint: 'rgba(255,255,255,0.12)',
    whiteMid: 'rgba(255,255,255,0.15)',
    whiteTenth: 'rgba(255,255,255,0.1)',
    whiteLight: 'rgba(255,255,255,0.2)',
    whiteBorder: 'rgba(255,255,255,0.32)',
    whiteDim: 'rgba(255,255,255,0.45)',
    whiteSoft: 'rgba(255,255,255,0.7)',
    whiteSubtitle: 'rgba(255,255,255,0.8)',
    whiteStrong: 'rgba(255,255,255,0.82)',
    whiteLabel: 'rgba(255,255,255,0.88)',
    whiteText: 'rgba(255,255,255,0.9)',
    whiteNear: 'rgba(255,255,255,0.95)',
    whiteCard: 'rgba(255,255,255,0.96)',
    primaryBorder: 'rgba(37,99,235,0.5)',
    darkHover: 'rgba(0,0,0,0.08)',
    dark45: 'rgba(0,0,0,0.45)',
    dark55: 'rgba(0,0,0,0.55)',
    dark80: 'rgba(0,0,0,0.8)'
} as const;

export const gradients = {
    // Pale blue band deepening to the right — the light-theme section ground (GlanceStats,
    // `sectionBackgrounds.module.scss` → `lightGradient`). Keep the two in step.
    lightBand: `linear-gradient(100deg, ${colors.background.subtle} 0%, ${colors.background.tag} 60%, ${colors.accent.blueBorder} 100%)`,
    darkSection: `linear-gradient(0deg, ${colors.text.primary} 0%, ${colors.primary.navy} 50%, ${colors.primary.deep} 100%)`,
    primaryActive: 'linear-gradient(90deg, #244486, #252e4f)',
    primaryButton: 'linear-gradient(270deg, #244486 0%, #252e4f 100%)',
    banner: 'linear-gradient(180deg, #F5F7FB 0%, #E4ECF8 100%)',
    // Home hero ground. The desktop version runs left-to-right behind the video; phones
    // have no video, so they take the brand navy running top-to-bottom across the whole
    // section instead of the slate wash meant to sit under footage.
    heroBanner: 'linear-gradient(to right, #151a22 0%, #2d3a4a 45%, #4a5c72 100%)',
    heroBannerMobile: `linear-gradient(180deg, ${colors.primary.deepMidnight} 0%, ${colors.primary.royalNavy} 100%)`,
    milestoneYears: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    milestoneSolutions: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    milestoneRevenue: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    milestoneExperts: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    complianceRail: `linear-gradient(180deg, ${colors.primary.deep} 0%, ${colors.primary.navy} 55%, ${colors.text.primary} 100%)`,
    navyCard: `linear-gradient(to top, ${colors.primary.deepMidnight}, ${colors.primary.royalNavy})`,
    // Flat-toned navy panel: one hue at two alphas on a 160deg axis, so the panel
    // reads as a single colour with depth rather than a two-colour ramp.
    navyPanelSoft: `linear-gradient(160deg, ${colors.primary.deep} 8.49%, rgba(31, 63, 122, 0.867) 91.51%)`,
    navyCardDeep: `linear-gradient(180deg, ${colors.primary.deep} 0%, ${colors.primary.midnightDeep} 100%)`,
    badgeDivider: 'linear-gradient(180deg, #123060 0%, #C9E1F9 50%, #123060 100%)',
    darkSectionOverlay: 'linear-gradient(0deg, rgba(15, 15, 16, 0.8) 0%, rgba(19, 42, 82, 0.8) 50%, rgba(31, 63, 122, 0.8) 100%)',
    // Split-hero review badge cards: a soft radial that lifts the centre of the card off
    // the photograph behind it.
    heroBadgeCard: 'radial-gradient(50% 50% at 50% 50%, rgba(61, 76, 102, 0.5766) 0%, rgba(11, 36, 73, 0.837) 100%)',
    // Lighter take on `heroBadgeCard` for the split hero, where the cards sit over the
    // photograph itself and a near-opaque fill hides too much of it.
    heroBadgeCardTranslucent: 'radial-gradient(50% 50% at 50% 50%, rgba(61, 76, 102, 0.32) 0%, rgba(11, 36, 73, 0.52) 100%)',
    heroBadgeSaaS: 'linear-gradient(90deg, rgba(43,127,255,0.2) 0%, rgba(21,93,252,0.2) 100%)',
    heroBadgeMartech: 'linear-gradient(90deg, rgba(173,70,255,0.2) 0%, rgba(152,16,250,0.2) 100%)',
    techGlow: 'linear-gradient(135deg, rgba(43,127,255,0.1) 0%, rgba(173,70,255,0.1) 100%)',
    // AI-acceleration orb. The core sits slightly above centre so the sphere catches its
    // light from the top rather than looking evenly backlit.
    aiOrb: `radial-gradient(circle at 50% 42%, ${colors.primary.link} 0%, ${colors.primary.navy} 100%)`,
    blogIndigoOrb: 'radial-gradient(circle, rgba(129, 140, 248, 1) 0%, rgba(0, 0, 0, 0) 70%)',
    blogSkyOrb: 'radial-gradient(circle, rgba(56, 189, 248, 0.4) 0%, rgba(0, 0, 0, 0) 70%)'
} as const;

export const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
} as const;

export const letterSpacing = {
    tight: '-1px',
    snug: '-0.5px',
    normal: '0',
    mid: '0.6px',
    eyebrow: '0.8px',
    em: '0.01em',
    wide: '0.05em',
    wider: '1.2px',
    widest: '2px'
} as const;

export const transition = {
    fast: '150ms ease',
    base: '200ms ease',
    medium: '250ms ease',
    slow: '300ms ease'
} as const;

export const fontFamily = {
    heading: "'Headers', sans-serif",
    body: "'RegularText', sans-serif",
    serif: "'Georgia', serif",
    // System monospace stack — small numbered/kicker labels; no web font to load.
    mono: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace"
} as const;

export const zIndex = {
    base: 0,
    raised: 1,
    overlay: 10,
    elevated: 20
} as const;

export const contentWidth = {
    xs: '25rem',
    screenshot: '37.5rem',
    tableMin: '56.25rem'
} as const;

export const contentMaxWidth = {
    prose: {xs: '100%', md: '37.5rem'},
    narrow: {xs: '100%', md: '48rem'},
    standard: {xs: '100%', md: '64rem'},
    wide: {xs: '100%', md: '75rem'},
    full: '100%'
} as const;

export const borderWidth = {
    thin: '0.0625rem',
    thick: '0.125rem'
} as const;

export const decorSize = {
    blur: '4rem',
    statMin: '7.5rem',
    orbSm: '16rem',
    orbMd: '24rem',
    orbLg: '37.5rem',
    orbBlur: '15rem'
} as const;

export const mediaWidth = {
    lg: '37.5rem'
} as const;

export const mediaSize = {
    serviceBanner: {width: 400, height: 380}
} as const;

export const typography = {
    sectionHeading: {
        fontWeight: fontWeight.bold,
        fontSize: {xs: fontSize[32], md: fontSize[48]},
        lineHeight: {xs: lineHeight[40], md: lineHeight[56]}
    },
    sectionHeadingLg: {
        fontWeight: fontWeight.extrabold,
        fontSize: {xs: fontSize[32], md: fontSize[48]},
        lineHeight: {xs: lineHeight[40], md: lineHeight[56]}
    },
    cardTitle: {
        fontWeight: fontWeight.bold,
        fontSize: {xs: fontSize[20], md: fontSize[24]},
        lineHeight: {xs: lineHeight[28], md: lineHeight[32]}
    },
    body: {fontWeight: fontWeight.regular, fontSize: fontSize[16], lineHeight: lineHeight[28]},
    bodySm: {fontWeight: fontWeight.regular, fontSize: fontSize[14], lineHeight: lineHeight[24]},
    caption: {fontWeight: fontWeight.medium, fontSize: fontSize[14], lineHeight: lineHeight[20]},
    cardLabel: {fontWeight: fontWeight.bold, fontSize: fontSize[18], lineHeight: lineHeight[28]}
} as const;

export const lineHeightRatio = {
    none: 1,
    flat: 1.1,
    tight: 1.2,
    snug: 1.4,
    normal: 1.5,
    relaxed: 1.6,
    loose: 1.7,
    looser: 1.75
} as const;

export const proseLinkSx = {
    '& p a, & li a': {
        color: colors.primary.link,
        textDecoration: 'underline'
    }
} as const;

export const breakpoints = {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536
} as const;

export type Breakpoint = keyof typeof breakpoints;
